import { Link } from '@tanstack/react-router';
import { useAuthStore } from '../store/auth.ts';
import { ThemeToggle } from './ThemeToggle.tsx';
import { Icon } from './Icon.tsx';
import { dv } from '../i18n/dv.ts';
import { displayName } from '../lib/utils.ts';

// Material 3 top app bar (desktop) — mark + wordmark on the left, theme /
// sign-out / avatar on the right. Ported from WapSign. These controls used to
// live inside the nav rail; moving them up here is what lets the rail carry
// nothing but destinations, as M3 navigation drawers do.
export function TopBar(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  if (!user) return null;
  const shownName = displayName(user);
  const initials = (shownName || user.email).slice(0, 2).toUpperCase();

  return (
    <header
      className="hidden md:flex fixed top-0 inset-x-0 z-40 h-16 items-center gap-2 pl-3 pr-8 bg-[hsl(var(--chrome))]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <Link to="/" className="ml-2 flex min-w-0 items-center gap-3">
        <img src="/icons/wapstat-mark.svg" alt="" className="size-10 shrink-0 rounded-[25%] object-contain" />
        <span className="flex min-w-0 flex-col">
          <span className="whitespace-nowrap text-xl font-medium leading-tight text-ink">{dv.appName}</span>
          {user.orgName && (
            <span className="truncate text-xs leading-tight text-muted-foreground">{user.orgName}</span>
          )}
        </span>
      </Link>

      <div className="flex-1" />

      <div className="flex shrink-0 items-center gap-1">
        <ThemeToggle />
        <button
          onClick={logout}
          aria-label={dv.auth.logout}
          className="grid size-11 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted/70"
        >
          <Icon name="logout" size={22} />
        </button>
        <Link
          to="/profile"
          aria-label={dv.nav.profile}
          title={shownName || undefined}
          className="ml-1 grid size-9 place-items-center rounded-full bg-[hsl(var(--tertiary-container))] text-sm font-medium text-[hsl(var(--on-tertiary-container))]"
        >
          {initials}
        </Link>
      </div>
    </header>
  );
}
